import type { PmsSourceKey } from '@/types/pms.types';
import type { VendorDefinition, VendorSettingsContext } from './types';
import { IntegrationOverview } from '../pms-configuration/integration-overview/IntegrationOverview';
import { IntegrationOverviewAppfolio } from '@/components/integrations/appfolio/integration-overview-appfolio/IntegrationOverviewAppfolio';
import { IntegrationOverviewBuildium } from '@/components/integrations/buildium/integration-overview-buildium/IntegrationOverviewBuildium';
import RentvineConnectionPanel from './panels/RentvineConnectionPanel';
import AppfolioConnectionPanel from './panels/AppfolioConnectionPanel';
import { usesOAuthView } from './credentialKind';
import AppfolioAutoSyncConfirmModal from './panels/AppfolioAutoSyncConfirmModal';
import BuildiumConnectionPanel from './panels/BuildiumConnectionPanel';
import { integrationStatusCopy } from './status/copy';
import { toVendorKey, VENDOR_DISPLAY_NAMES } from './vendorDisplayName';
import rentvineLogo from '@/asset/images/rentvine-logo.svg';
import appfolioLogo from '@/asset/images/appfolio-logo.svg';
import buildiumLogo from '@/asset/images/buildium-logo.svg';

/** DV-2549 §2 decision 6: "Save changes" while disabled, "Save & sync properties" otherwise — same for every vendor. */
const saveLabelFor = (ctx: VendorSettingsContext) =>
  ctx.integrationStatus.kind === 'disabled'
    ? integrationStatusCopy.saveLabels.saveChanges
    : integrationStatusCopy.saveLabels.saveAndSync;

const credentialIssue = (vendor: string) =>
  `Issue connecting with ${vendor}. Check and update the connection and credentials.`;

export const vendorRegistry: Record<PmsSourceKey, VendorDefinition> = {
  rentvine: {
    key: 'rentvine',
    displayName: VENDOR_DISPLAY_NAMES.rentvine,
    logo: rentvineLogo,
    logoHeightPx: 20,
    directoryDescription:
      'A 2-way sync that brings all your properties into PlanOmatic for a simple ordering experience, and delivers your photos directly into your Rentvine platform.',
    connectNote: 'Subdomain, API Key and API Secret',
    inactiveMessage: credentialIssue('Rentvine'),
    ConnectionPanel: RentvineConnectionPanel,
    OverviewTab: IntegrationOverview,
    showSettings: () => true,
    settingsDisabled: () => false,
    saveLabel: saveLabelFor,
  },
  appfolio: {
    key: 'appfolio',
    displayName: VENDOR_DISPLAY_NAMES.appfolio,
    logo: appfolioLogo,
    logoHeightPx: 22,
    directoryDescription:
      'A 2-way sync that integrates your properties with PlanOmatic for easy ordering and delivers your photos directly to AppFolio.',
    connectNote: 'Subdomain only',
    inactiveMessage: credentialIssue('AppFolio'),
    ConnectionPanel: AppfolioConnectionPanel,
    OverviewTab: IntegrationOverviewAppfolio,
    // Old AppFolio page: settings shown for admin, OAuth view, or once a legacy connection resolved.
    showSettings: (ctx) =>
      ctx.isInternalAdmin || usesOAuthView(ctx.vendorData?.secret) || ctx.connectionState.kind !== 'idle',
    settingsDisabled: () => false,
    toggleGuard: (type, edited) => type === 'autoSync' && !edited.autoSyncAssets,
    ToggleConfirmModal: AppfolioAutoSyncConfirmModal,
    saveLabel: saveLabelFor,
  },
  buildium: {
    key: 'buildium',
    displayName: VENDOR_DISPLAY_NAMES.buildium,
    logo: buildiumLogo,
    logoHeightPx: 26,
    directoryDescription:
      'A 2-way sync that integrates your properties with PlanOmatic for easy ordering and delivers your photos directly to Buildium.',
    connectNote: 'Authorize through the Buildium marketplace',
    inactiveMessage: 'Issue connecting with Buildium. Check the permissions.',
    shouldBootstrap: (search) => search.get('token') === null,
    ConnectionPanel: BuildiumConnectionPanel,
    OverviewTab: IntegrationOverviewBuildium,
    showSettings: (ctx) => ctx.connectionState.kind !== 'idle',
    settingsDisabled: (ctx) => ctx.connectionState.kind === 'failed',
    saveLabel: saveLabelFor,
    // The retired footer button carried this guard: a failed Buildium
    // connection has nothing the user can fix from this page (the marketplace
    // token isn't pasteable), so saving is blocked until it recovers.
    saveDisabled: (ctx) => ctx.connectionState.kind === 'failed',
  },
};

export const getVendorDefinition = (id: string | undefined): VendorDefinition | undefined => {
  const key = toVendorKey(id);
  return key ? vendorRegistry[key] : undefined;
};
